import settings, { SUBMIT_PRESCREENING_POST_URL } from '@/constants/constants';
import { FF_DISABLE_CONSULTATION_BUTTONS } from '@/constants/featureFlags';
import ffDefaults from '@/constants/ffDefaults';
import { preScreeningSteps } from '@/data/PreScreening/pre-screening-steps';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import SystemUnavailable from '@/pages/components/SystemUnavailable';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { restoreObject } from '@/utils/restoreObject';
import { getBrowserTimezone } from '@/utils/timezone';
import { FormBuilder } from '@montugroup/form-builder';
import { Box, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WithPageWrapper } from '../components/Branding';
import Calendar from '../components/Calendar';

export const PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY = 'prescreeningUserEmail';

type Props = {
  urlParams?: string | Record<string, string> | URLSearchParams;
  callback?: (event: string, data: FieldValues) => void;
  onSubmit?: (data: FieldValues) => Promise<void>;
  pageWrapper?: boolean;
};

export default function PreScreeningForm({ urlParams, callback, onSubmit, pageWrapper = true }: Props) {
  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();
  const [emailAddress, setEmailAddress] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { flags, loading } = useFeatureFlags();

  const POST_URL = `${settings.xApiUrl}${SUBMIT_PRESCREENING_POST_URL}`;

  const devmodeShowCalendar = Boolean(searchParams.get('cal'));
  const showImmediateNurseConsultPage = Boolean(searchParams.get('contactNurse'));

  const promocode = searchParams.get('promocode');
  const preloadCalendarAsHidden = Boolean(emailAddress && promocode);

  const queryParams = new URLSearchParams(urlParams || location.search);
  const { eventType, step, hasSubmittedForm, study, ...formData } = Object.fromEntries(queryParams);

  useEffect(() => {
    if (emailAddress) {
      localStorage.setItem(PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY, emailAddress);
    }
  }, [emailAddress]);

  useEffect(() => {
    if (hasSubmittedForm && formData) {
      const restoreData = restoreObject(formData) as FieldValues;
      setEmailAddress(restoreData?.email_and_privacy?.email.toLowerCase() || '');
    }
  }, [hasSubmittedForm, formData]);

  const handleCallback = useCallback(
    (event: string, data: FieldValues) => {
      // Get email address and store it in state to be used in Calendar booking form autofill
      const email = data?.email_and_privacy?.email.toLowerCase();

      if (email) {
        setEmailAddress(email);
      }

      if (callback) {
        callback(event, data);
      }
    },
    [callback],
  );

  const handleAfterSubmitSuccess = useCallback(
    (formValues: FieldValues, responseBody?: object) => {
      const patientEmail = formValues?.data?.emailAndPrivacy?.email ?? emailAddress;
      // The API decides which consult flow the patient belongs in.
      const { consultFlow } = (responseBody ?? {}) as { consultFlow?: 'd2d' | 'inc' };
      // Carried through rather than rebuilt, so `promocode` reaches the nurse
      // and doctor forms that read it.
      const params = new URLSearchParams(window.location.search);
      params.set('email', patientEmail);
      // The nurse flow is where consult-eligible sends a patient with no flow
      // anyway, and setting it unconditionally means a stale one on the URL
      // cannot survive.
      params.set('consultFlow', consultFlow ?? 'inc');

      navigate(`/consult-eligible?${params.toString()}`);
    },
    [emailAddress, navigate],
  );

  const logEvents = useCallback(
    (event: string, eventData: FieldValues) => {
      const stepIndex = eventData.current;

      if (event === 'step_change') {
        // The consult flow decides where an eligible patient goes next, so the
        // redirect happens in afterSubmitSuccess rather than here.
        if (stepIndex === 8 && eventData.next === 8) {
          return;
        }

        // track event for not eligible paths
        if (eventData.next === 8) {
          const payload = {
            event: 'prescreening_complete',
            step_name: 'End of Screening',
            clickId: 'not-eligible',
            result: 'Not Eligible',
            step_number: stepIndex,
          };

          sendGoogleAnalyticsEvent(event, payload);
        }
      }

      // Handle "prescreening complete" event
      if (event === 'submission_success') {
        const payload = {
          event: 'prescreening_complete',
          step_name: 'End of Screening',
          clickId: 'book-consultation',
          result: 'Eligible',
          step_number: stepIndex,
        };

        sendGoogleAnalyticsEvent(event, payload);
      }

      // Handle normal step event (called after the completion of the step)
      const prevTrackingData = preScreeningSteps[stepIndex - 1]?.trackingData;

      if (!prevTrackingData) {
        return;
      }

      const prevPayload = {
        clickId: prevTrackingData.clickId ?? null,
        event: prevTrackingData.event ?? null,
        step_name: prevTrackingData.stepName ?? null,
        ...(eventData?.values?.email_and_privacy?.email
          ? { sha_email: formatStringToSha256Hash(eventData.values.email_and_privacy?.email) }
          : {}),
        step_number: String(eventData.current),
      };

      handleCallback(event, eventData?.values ?? {});
      sendGoogleAnalyticsEvent(event, prevPayload);
    },
    [handleCallback, sendGoogleAnalyticsEvent],
  );

  const handleBeforeSubmit = useCallback(
    (formValues: FieldValues) => {
      const landingPage = study ? `form/${String(study)}` : 'form';

      return {
        data: {
          medicalCondition: formValues.medical_condition,
          triedTreatment: formValues.tried_treatment,
          treatment: formValues.treatment,
          pregnant: formValues.pregnant,
          psychosis: formValues.psychosis,
          emailAndPrivacy: {
            email: formValues.email_and_privacy.email,
            acceptPrivacyNotice: formValues.email_and_privacy.accept_privacy_notice,
            subscribeProductServices: formValues.email_and_privacy.subscribe_product_services,
          },
          variant: formValues.variant,
          study,
          landingPage,
          timezone: getBrowserTimezone(),
        },
      };
    },
    [study],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const disableConsultations = flags[FF_DISABLE_CONSULTATION_BUTTONS] ?? ffDefaults[FF_DISABLE_CONSULTATION_BUTTONS];

  if (disableConsultations) {
    return <SystemUnavailable />;
  }

  return WithPageWrapper(
    <>
      {!devmodeShowCalendar && !showImmediateNurseConsultPage && (
        <FormBuilder
          formId="prescreening"
          steps={preScreeningSteps}
          activeStep={Number(step) || 0}
          defaultValues={eventType === 'restore' ? formData : undefined}
          logEvents={logEvents}
          postUrl={POST_URL}
          onSubmit={onSubmit}
          afterSubmitSuccess={handleAfterSubmitSuccess}
          transform={handleBeforeSubmit}
        />
      )}
      {(preloadCalendarAsHidden || devmodeShowCalendar) && (
        <Calendar showCalendar={devmodeShowCalendar} emailAddress={emailAddress} />
      )}
    </>,
    pageWrapper,
  );
}
